import React from 'react';
import '@/index.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { RecorderProvider } from './context/RecorderContext';
import AppShell from './components/jixxeo/AppShell';
import Landing from './pages/Landing';
import { LoginPage, RegisterPage } from './pages/Auth';
import HomeDashboard from './pages/HomeDashboard';
import ChatPage from './pages/ChatPage';
import ImageStudio from './pages/ImageStudio';
import VideoStudio from './pages/VideoStudio';
import Translator from './pages/Translator';
import Discover from './pages/Discover';
import ProfilePage from './pages/ProfilePage';
import AdminDashboard from './pages/AdminDashboard';
import Subscriptions from './pages/Subscriptions';
import { PaymentSuccess, PaymentCancel } from './pages/PaymentResult';
import { CashoutPage, SpinPage, MissionsPage, ReferralsPage, LeaderboardsPage, MarketplacePage, BoostsPage } from './pages/RewardsPages';
import { VoicePage, AIHub, CreateHub } from './pages/Hubs';
import ChatHistory from './pages/ChatHistory';
import MyRecordings from './pages/MyRecordings';
import InstallPage from './pages/InstallPage';
import MemberSearch from './pages/MemberSearch';
import CoinShop from './pages/CoinShop';
import CreditsWallet from './pages/CreditsWallet';
import Marketplace from './pages/Marketplace';
import MarketplaceMine from './pages/MarketplaceMine';
import Wallet from './pages/Wallet';
import NotificationSettings from './pages/NotificationSettings';
import PushPermissionBanner from './components/jixxeo/PushPermissionBanner';
import AdminProfileConnections from './pages/AdminProfileConnections';

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="text-center py-20 text-slate-400">Loading…</div>;
  return user ? <HomeDashboard /> : <Landing />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  return children;
}

function AdminGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || !user.is_admin) return <Navigate to="/" />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <RecorderProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-center" />
          <PushPermissionBanner />
          <Routes>
          <Route element={<AppShell />}>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/ai" element={<AIHub />} />
            <Route path="/create" element={<CreateHub />} />
            <Route path="/ai/chat" element={<ChatPage tool="chat" />} />
            <Route path="/ai/writing" element={<ChatPage tool="writing" />} />
            <Route path="/ai/code" element={<ChatPage tool="coding" />} />
            <Route path="/ai/homework" element={<ChatPage tool="homework" />} />
            <Route path="/ai/research" element={<ChatPage tool="research" />} />
            <Route path="/ai/translate" element={<Translator />} />
            <Route path="/ai/image" element={<ImageStudio />} />
            <Route path="/ai/video" element={<VideoStudio />} />
            <Route path="/ai/voice" element={<VoicePage />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/history" element={<Protected><ChatHistory /></Protected>} />
            <Route path="/recordings" element={<Protected><MyRecordings /></Protected>} />
            <Route path="/install" element={<InstallPage />} />
            <Route path="/search" element={<MemberSearch />} />
            <Route path="/coinshop" element={<CoinShop />} />
            <Route path="/credits" element={<Protected><CreditsWallet /></Protected>} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/marketplace/mine" element={<Protected><MarketplaceMine /></Protected>} />
            <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
            <Route path="/notifications/settings" element={<Protected><NotificationSettings /></Protected>} />
            <Route path="/admin/users/:uid" element={<AdminGuard><AdminProfileConnections /></AdminGuard>} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/leaderboards" element={<LeaderboardsPage />} />
            <Route path="/subscriptions" element={<Subscriptions />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
            <Route path="/profile/:username" element={<ProfilePage />} />
            <Route path="/cashout" element={<Protected><CashoutPage /></Protected>} />
            <Route path="/spin" element={<Protected><SpinPage /></Protected>} />
            <Route path="/missions" element={<Protected><MissionsPage /></Protected>} />
            <Route path="/referrals" element={<Protected><ReferralsPage /></Protected>} />
            <Route path="/boosts" element={<Protected><BoostsPage /></Protected>} />
            <Route path="/settings" element={<Protected><ProfilePage /></Protected>} />
            <Route path="/admin" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
          </Route>
        </Routes>
      </BrowserRouter>
      </RecorderProvider>
    </AuthProvider>
  );
}

export default App;
